import { DocumentProperty } from 'types/dynamic-forms/DocumentProperty';
import { JsonPointerPath } from 'types/dynamic-forms/JsonPointerPath';
import { ResolveValueContext } from './ResolveValueContext';
import { notEmptyValidationRuleResolver } from './validation-resolvers/notEmptyValidationRuleResolver';
import { mustBeTrueValidationRuleResolver } from './validation-resolvers/mustBeTrueValidationRuleResolver';
import { notEmptyHtmlValidationRuleResolver } from './validation-resolvers/notEmptyHtmlValidationRuleResolver';
import { BaseValidationRuleResolver } from './validation-resolvers/BaseValidationRuleResolver';
import { compareToValidationRuleResolvers } from './validation-resolvers/compareToValidationRuleResolvers';
import { compositeRuleValidationRuleResolver } from './validation-resolvers/compositeRuleValidationRuleResolver';
import { materialPropertiesListValidationRuleResolver } from './validation-resolvers/materialPropertiesListValidationRuleResolver';
import { lengthValidationRuleResolver } from './validation-resolvers/lengthValidationRuleResolver';
import { verifierNotInPractitionersValidationRuleResolver } from './validation-resolvers/verifierNotInPractitionersValidationRuleResolver';
import { arrayContainsByFieldValidationRuleResolver } from './validation-resolvers/arrayContainsByFieldValidationRuleResolver';
import { allOrNoneValidationRuleResolver } from './validation-resolvers/allOrNoneValidationRuleResolver';
import { dateAfterRefValidationRuleResolver } from './validation-resolvers/dateAfterRefValidationRuleResolver';

type OwnAndNestedErrorsNode = { ownError: string | null; childrenErrors?: Map<string | number, OwnAndNestedErrorsNode> };

const resolversMap = new Map<string, BaseValidationRuleResolver>([
  ['notEmpty', notEmptyValidationRuleResolver],
  ['mustBeTrue', mustBeTrueValidationRuleResolver],
  ['notEmptyHtml', notEmptyHtmlValidationRuleResolver],
  ['>', compareToValidationRuleResolvers['>']],
  ['>=', compareToValidationRuleResolvers['>=']],
  ['<', compareToValidationRuleResolvers['<']],
  ['<=', compareToValidationRuleResolvers['<=']],
  ['compositeRule', compositeRuleValidationRuleResolver(resolveValidationRule)],
  ['length', lengthValidationRuleResolver],
  ['materialPropertiesList', materialPropertiesListValidationRuleResolver],
  ['verifierNotInPractitioners', verifierNotInPractitionersValidationRuleResolver],
  ['arrayContainsByField', arrayContainsByFieldValidationRuleResolver],
  ['allOrNone', allOrNoneValidationRuleResolver],
  ['dateAfterRef', dateAfterRefValidationRuleResolver],
]);

export function validateDocument(context: ResolveValueContext) {
  const result = new Map<string, OwnAndNestedErrorsNode>();

  for (const prop of context.properties) {
    const propErrors = getOwnAndNestedErrors([prop.propertyId], prop, context);
    result.set(prop.propertyId, propErrors);
  }

  return result;
}

function getOwnAndNestedErrors(
  path: JsonPointerPath,
  prop: DocumentProperty,
  context: ResolveValueContext
): OwnAndNestedErrorsNode {
  if (context.shouldIgnoreValidation(path)) {
    return { ownError: null };
  }

  const value = context.getValue(path);
  const ownError = getPropertyError(value, path, prop, context);

  if (!prop.children) {
    return { ownError };
  }

  const childrenErrors = new Map<string | number, OwnAndNestedErrorsNode>();

  // Handle Array type: validate each object in the array
  if (prop.type === 'array') {
    // Validate each object in the array
    for (let elementIndex = 0; elementIndex < value.length; elementIndex++) {
      const elementErrors = new Map<string, OwnAndNestedErrorsNode>();

      // The element itself can't have errors, but its properties can
      childrenErrors.set(elementIndex, { ownError: null, childrenErrors: elementErrors });
      for (const childProp of prop.children) {
        elementErrors.set(
          childProp.propertyId,
          getOwnAndNestedErrors([...path, elementIndex, childProp.propertyId], childProp, context)
        );
      }
    }
  } else if (prop.type === 'object') {
    // Handle Object type: validate the single object's children directly.
    // Unlike arrays, object children are addressed without an index segment
    // (e.g. ['preVerifiedTool', 'id']), so store them directly under childrenErrors
    // keyed by propertyId to match getValue/getErrorMessage path traversal.
    for (const childProp of prop.children) {
      childrenErrors.set(childProp.propertyId, getOwnAndNestedErrors([...path, childProp.propertyId], childProp, context));
    }
  }

  return { ownError, childrenErrors };
}

function getPropertyError(
  value: any,
  path: JsonPointerPath,
  property: DocumentProperty,
  context: ResolveValueContext
): string | null {
  if (property.validationRule) {
    const directError = resolveValidationRule(value, property.validationRule, property, path, context);
    if (directError) {
      return directError;
    }
  }

  if (property.dataSourceId) {
    // For datasources we want to validate that their values are inside options
    // IIRC this is always needed
    if (value === null) {
      return null;
    }

    const options = context.getOptions(path);
    if (!options) {
      throw new Error(`Data source "${property.dataSourceId}" doesn't exist.`);
    }

    const isValueInOptions = options.some((o) => o.value === value);
    return !isValueInOptions ? 'Invalid value' : null;
  }

  return null;
}

function resolveValidationRule(
  value: any,
  rule: any,
  property: DocumentProperty,
  path: JsonPointerPath,
  context: ResolveValueContext
): string | null {
  // `isHtml` selects the semantic rule, while the editor/backend boundary guarantees that empty
  // rich text is represented by "". The frontend deliberately does not parse arbitrary HTML here.
  const ruleTypeName = rule.__typename === 'notEmpty' && property.isHtml ? 'notEmptyHtml' : rule.__typename;

  const resolver = resolversMap.get(ruleTypeName);
  if (!resolver) {
    throw new Error(`Unknown validation rule: '${JSON.stringify(rule)}'.`);
  }

  return resolver(value, rule, property, path, context);
}

export function* iterateErrors(node: OwnAndNestedErrorsNode): Generator<string> {
  if (node.ownError) {
    yield node.ownError;
  }

  if (node.childrenErrors) {
    for (const child of node.childrenErrors.values()) {
      for (const childrenError of iterateErrors(child)) {
        yield childrenError;
      }
    }
  }
}
